import React from 'react';
import { useLocation } from '@tanstack/react-router';
import { Alert, Button } from '@trussworks/react-uswds';
import { mdiAccountOutline, mdiEmailOutline, mdiHelpCircleOutline } from '@mdi/js';
import classNames from 'classnames';
import Icon from '@components/icon/Icon';
import { useUserProfile } from '@hooks';
import { useCancelHelpInquiryStatusMutation, useHelpStatusQuery } from '@api/services/help/hooks';
import { getAppStore } from '@src/utils/store-reference';
import { AssistanceOption } from './types';
import {
  canAccessRegulatorAssistance,
  getVisibleAssistanceOptions,
  supportEmailHref,
  supportEmailHours,
} from './helpers';
import { useModal, useSupportDrawer } from '@stores';
import { getEnvironmentVisibility } from '@components/environment-visibility/EnvironmentVisibility';
import CancelHelpModal from '@src/app-pages/Dashboard/components/request-table/RequestCancelHelpModal';
import { HelpRequestStatuses } from '@src/utils/enums';

type IntroProps = {
  onContinue: () => void;
  className?: string;
  showSelfHelp?: boolean;
};

const appBasePath = import.meta.env.BASE_URL.replace(/\/$/, '');
const supportPagePath = `${appBasePath}/support`;
const existingHelpInquiryMessage = 'An open or in-progress help inquiry already exists for this request.';

const assistanceOptions: AssistanceOption[] = [
  {
    id: 'selfHelp',
    title: 'Self Help',
    reason: 'Review the FAQ page for common questions and guidance.',
    icon: mdiHelpCircleOutline,
  },
  {
    id: 'rrsSupport',
    title: 'RRS Support',
    reason:
      'Get help with RRS technical or application issues, such as account access, permissions, saving, editing, submitting, application errors, or file uploads including GDB files.',
    icon: mdiEmailOutline,
  },
  {
    id: 'regulatorAssistance',
    title: 'USACE Regulator Assistance',
    reason:
      'Get help with regulatory or permitting questions, such as jurisdiction, regulatory requirements, what information to provide, or whether a pre-application request may be appropriate.',
    icon: mdiAccountOutline,
  },
];

const handleOpenSupport = (event: React.MouseEvent<HTMLButtonElement>) => {
  event.preventDefault();
  event.stopPropagation();
  window.open(supportPagePath, '_blank');
};

const Intro: React.FC<IntroProps> = ({ onContinue, className, showSelfHelp = true }) => {
  const { userRole } = useUserProfile();
  const isDrawerOpen = useSupportDrawer((state) => state.isDrawerOpen);
  const { openModal } = useModal();

  const currentPathname = useLocation({
    select: (location) => location.pathname,
  });

  const store = getAppStore();
  const requestAPIData = store.selectRequestAPIData?.();
  const requestFormData = store.selectRequestFormData?.();
  const requestStatus = store.selectRequestStatus?.();
  const requestId = store.selectRequestID?.();
  const version = store.selectVersion?.();
  const requestType = store.selectRequestType?.();
  const permission = requestAPIData?.request?.permission;
  const helpInquiryId = requestFormData?.request?.helpInquiryID;
  const isHelpInquiryStatusOpen = requestFormData?.request?.helpInquiryStatus === HelpRequestStatuses.Open;
  const canCancelHelpInquiry = helpInquiryId && isHelpInquiryStatusOpen;

  const hasRegulatorAssistanceAccess =
    permission && requestType && userRole && requestStatus
      ? canAccessRegulatorAssistance({
          permission,
          requestType,
          userRole,
          requestStatus,
        })
      : false;
  const isFormPage = currentPathname === '/forms' || currentPathname.startsWith('/forms/');
  const showRegulatorAssistance =
    getEnvironmentVisibility({ mode: 'hide', prod: true }) && //Temporarily hide Regulator Assistance in PROD
    isFormPage &&
    isFormPage &&
    hasRegulatorAssistanceAccess;

  const { data: helpStatus, isLoading: isHelpStatusLoading } = useHelpStatusQuery({
    enabled: isDrawerOpen && showRegulatorAssistance,
    requestId,
    version,
    helpInquiryStatus: requestFormData?.request?.helpInquiryStatus,
  });

  const { mutate: cancelHelpInquiry } = useCancelHelpInquiryStatusMutation({
    onSuccess: () => {
      store.doUpdateRequestObject({
        helpInquiryStatus: HelpRequestStatuses.Closed,
      });
    },
  });

  const canRequestAssistance = helpStatus?.canRequestAssistance !== false;
  const shouldShowExistingHelpInquiryWarning = showRegulatorAssistance && !isHelpStatusLoading && !canRequestAssistance;
  const canSubmitRegulatorAssistance = showRegulatorAssistance && !isHelpStatusLoading && canRequestAssistance;

  const visibleAssistanceOptions = getVisibleAssistanceOptions(assistanceOptions, showRegulatorAssistance).filter(
    (option) => showSelfHelp || option.id !== 'selfHelp'
  );
  const containerStyles = classNames('request-assistance-intro', className);

  return (
    <div className={containerStyles}>
      <h3 className='request-assistance-intro__heading'>How can we help?</h3>

      <div className='request-assistance-card-list' aria-label='Assistance options'>
        {visibleAssistanceOptions.map((option) => (
          <div className='request-assistance-card' key={option.id}>
            <div className='request-assistance-card__icon-container'>
              <Icon
                path={option.icon}
                size={1.4}
                focusable={false}
                aria-hidden='true'
                role='presentation'
                className='request-assistance-card__icon'
              />
            </div>

            <div className='request-assistance-card__content'>
              <p className='request-assistance-card__title'>{option.title}</p>
              <p className='request-assistance-card__description'>{option.reason}</p>

              <div className='request-assistance-card__action'>
                {option.id === 'selfHelp' && (
                  <Button type='button' onClick={handleOpenSupport} className='request-assistance-card__button'>
                    Go to FAQs & guidance
                  </Button>
                )}

                {option.id === 'rrsSupport' && (
                  <>
                    <a href={supportEmailHref} className='usa-button request-assistance-card__button'>
                      Email RRS Support
                    </a>

                    <p className='text-base text-italic margin-top-1 margin-bottom-0'>{supportEmailHours}</p>
                  </>
                )}

                {option.id === 'regulatorAssistance' && (
                  <>
                    {shouldShowExistingHelpInquiryWarning && (
                      <Alert
                        type='warning'
                        heading='Help inquiry already exists'
                        headingLevel='h4'
                        slim
                        className='margin-bottom-2'
                      >
                        {existingHelpInquiryMessage}
                      </Alert>
                    )}
                    {canCancelHelpInquiry && (
                      <Button
                        type='button'
                        onClick={() =>
                          openModal(CancelHelpModal, {
                            cancelClick: () => cancelHelpInquiry({ requestId, version, helpInquiryId }),
                          })
                        }
                        className='request-assistance-card__button margin-bottom-1'
                      >
                        Cancel Help Inquiry
                      </Button>
                    )}
                    <Button
                      type='button'
                      onClick={onContinue}
                      className='request-assistance-card__button'
                      disabled={!canSubmitRegulatorAssistance}
                    >
                      Request Regulator Assistance
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Intro;
